import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERIOD_DURATION_MINUTES, PERIOD_TIMES, PERIOD_TIMES_ARE_PLACEHOLDER } from '../config/periods';
import { BackArrowIcon, BoardIcon, ClockIcon } from './icons';

export interface TopBarProps {
  className?: string;
  lessonTitle?: string;
  statusLabel?: string;
  date: string;
  index: number;
  onClose: () => void;
}

export function TopBar({
  className,
  lessonTitle,
  statusLabel,
  date,
  index,
  onClose,
}: TopBarProps) {
  const { t } = useTranslation('layout');
  const startedAt = useRef(Date.now());
  const [remaining, setRemaining] = useState(PERIOD_DURATION_MINUTES * 60);

  useEffect(() => {
    function update() {
      let endAt = startedAt.current + PERIOD_DURATION_MINUTES * 60 * 1000;
      if (!PERIOD_TIMES_ARE_PLACEHOLDER && PERIOD_TIMES[index]) {
        endAt = new Date(`${date}T${PERIOD_TIMES[index]}:00+07:00`).getTime() + PERIOD_DURATION_MINUTES * 60 * 1000;
      }
      setRemaining(Math.max(0, Math.ceil((endAt - Date.now()) / 1000)));
    }
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [date, index]);

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const timerLabel = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return (
    <header className="shell__topbar">
      <button type="button" className="topbar__back" aria-label={t('topbar.close')} onClick={onClose}>
        <BackArrowIcon />
      </button>
      {/* label only - the back button is the single way to end the lesson */}
      <span className="topbar__class">{className || t('topbar.classFallback')}</span>

      <span className="topbar__divider" />

      <div className="topbar__lesson">
        <span className="topbar__lesson-icon" aria-hidden={true}>
          <BoardIcon size={18} />
        </span>
        <span className="topbar__lesson-title">{lessonTitle ?? t('topbar.lessonFallback')}</span>
      </div>

      <div className="topbar__spacer" />

      {/* status pill and timer read out only - neither is actionable */}
      <span className="topbar__status">
        <span className="topbar__status-dot" aria-hidden={true} />
        {statusLabel ?? t('topbar.statusTeaching')}
      </span>

      <span className="topbar__timer">
        <ClockIcon size={14} />
        <span className="topbar__timer-value">{timerLabel}</span>
      </span>
    </header>
  );
}
